<template>
  <div ref="questionDetail" class="question-detail">
    <!-- 返回按钮 -->
    <div class="back-button">
      <el-button icon="el-icon-arrow-left" @click="goBack">返回问题列表</el-button>
    </div>

    <!-- 问题详情 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="15" animated />
    </div>
    <div v-else-if="!question" class="not-found">
      <i class="el-icon-warning-outline"></i>
      <h2>问题不存在或已被删除</h2>
      <el-button type="primary" @click="goBack">返回问题列表</el-button>
    </div>
    <div v-else class="question-container">
      <!-- 问题头部 -->
      <div class="question-header">
        <h1 class="question-title">{{ question.title }}</h1>
        <div class="question-meta">
          <div class="meta-item">
            <i class="el-icon-view"></i>
            <span>{{ question.views }} 浏览</span>
          </div>
          <div class="meta-item">
            <i class="el-icon-chat-dot-round"></i>
            <span>{{ replies.length }} 回复</span>
          </div>
          <div class="meta-item">
            <i class="el-icon-star-off"></i>
            <span>{{ question.likes }} 点赞</span>
          </div>
          <div class="meta-item status" :class="{ 'solved': question.solved }">
            {{ question.solved ? '已解决' : '未解决' }}
          </div>
        </div>
      </div>

      <!-- 问题内容 -->
      <div class="question-content">
        <div class="author-info">
          <el-avatar :src="question.author_avatar || 'https://www.sweek.top/api/preview/personlog-avatar.jpg'"
            :size="50"></el-avatar>
          <div class="author-meta">
            <div class="author-name">{{ question.author_nickname || '匿名用户' }}</div>
            <div class="post-time">发布于 {{ formatTime(question.create_time) }}</div>
          </div>
          <div v-if="isAuthor || (userInfo?.role == 'admin')" class="question-actions">
            <!-- <el-button type="text" icon="el-icon-edit" @click="editQuestion">编辑</el-button> -->
            <el-button style="padding: 10px;background-color: #f1f1f1;" type="text" icon="el-icon-delete"
              @click="deleteQuestion">删除</el-button>
          </div>
        </div>

        <div class="content-body">
          <p>{{ question.content }}</p>

          <!-- 问题图片 -->
          <div v-if="question.images && question.images.length > 0" class="content-images">
            <el-image v-for="(img, index) in question.images" :key="index" :src="img"
              :preview-src-list="question.images">
            </el-image>
          </div>

          <!-- 问题标签 -->
          <div v-if="question.tags && question.tags.length > 0" class="content-tags">
            <el-tag v-for="(tag, index) in question.tags" :key="index" size="small" effect="plain">
              {{ tag }}
            </el-tag>
          </div>

          <div class="question-actions-footer">
            <!-- 点赞按钮 -->
            <el-button type="text" class="like-button" @click="toggleQuestionLike" :class="{ 'liked': questionLiked }">
              <i :class="questionLiked ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
              {{ questionLiked ? '已点赞' : '点赞' }} ({{ question.likes || 0 }})
            </el-button>

            <!-- 标记为已解决按钮 -->
            <div v-if="isAuthor && !question.solved" class="mark-solved-button">
              <el-button type="success" @click="handleMarkAsSolved">
                <i class="el-icon-check"></i> 标记为已解决
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 回复列表 -->
      <div class="replies-section">
        <div class="section-header">
          <h2>{{ replies.length }} 条回复</h2>
          <el-button type="primary" @click="showReplyDialog = true" v-if="!showReplyDialog">添加回复</el-button>
        </div>

        <div v-if="replies.length === 0" class="no-replies">
          <i class="el-icon-chat-line-square"></i>
          <p>暂无回复，成为第一个回复者吧！</p>
        </div>

        <div v-else class="replies-list">
          <div v-for="(reply, index) in replies" :key="reply.id" class="reply-item">
            <div class="reply-number">#{{ index + 1 }}</div>
            <div class="reply-content">
              <div class="reply-author">
                <el-avatar
                  :src="reply.author_avatar ? reply.author_avatar : 'https://www.sweek.top/api/preview/personlog-avatar.jpg'"
                  :size="40" style="border-radius: 50%; overflow: hidden;"></el-avatar>
                <div class="author-meta">
                  <div class="author-name">{{ reply.author_nickname ? reply.author_nickname : '匿名用户' }}</div>
                  <div class="post-time">{{ formatTime(reply.create_time) }}</div>
                </div>
                <div v-if="isAuthor || (userInfo?.role == 'admin')" class="reply-actions">
                  <!-- <el-button type="text" icon="el-icon-edit" @click="editReply(reply)">编辑</el-button> -->
                  <el-button style="padding: 10px;background-color: #f1f1f1;" type="text" icon="el-icon-delete"
                    @click="deleteReply(reply.id)">删除</el-button>
                </div>
              </div>

              <div class="reply-body">
                <p>{{ reply.content }}</p>

                <!-- 回复图片 -->
                <div v-if="reply.images && reply.images.length > 0" class="reply-images">
                  <el-image v-for="(img, imgIndex) in reply.images" :key="imgIndex" :src="img"
                    :preview-src-list="reply.images">
                  </el-image>
                </div>
              </div>

              <div class="reply-footer">
                <el-button type="text" class="like-button" @click="toggleReplyLike(reply.id)"
                  :class="{ 'liked': replyLikedSet.has(reply.id) }">
                  <i :class="replyLikedSet.has(reply.id) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                  {{ replyLikedSet.has(reply.id) ? '已点赞' : '点赞' }} ({{ reply.likes || 0 }})
                </el-button>
                <el-button type="text" @click="markAsSolution(reply.id)" v-if="isAuthor && !question.solved">
                  <i class="el-icon-check"></i> 采纳为最佳答案
                </el-button>
                <div v-if="question.best_answer_id === reply.id" class="solution-badge">
                  <i class="el-icon-check"></i> 最佳答案
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 回复对话框 -->
      <el-dialog title="添加回复" :visible.sync="showReplyDialog" :width="dialogWidth" :before-close="handleDialogClose"
        :append-to-body="true" :modal-append-to-body="true" custom-class="reply-dialog">
        <el-form :model="newReply" :rules="rules" ref="replyForm" :label-width="formLabelWidth">
          <el-form-item label="内容" prop="content">
            <el-input type="textarea" v-model="newReply.content" :rows="6" placeholder="请输入回复内容"></el-input>
          </el-form-item>
          <el-form-item label="图片">
            <el-upload action="https://www.sweek.top/api/upload" list-type="picture-card"
              :on-preview="handlePictureCardPreview" :on-remove="handleRemove" :on-success="handleUploadSuccess"
              :before-upload="beforeUpload" multiple class="reply-image-upload" name="images">
              <i class="el-icon-plus"></i>
            </el-upload>
            <el-dialog :visible.sync="dialogVisible" append-to-body>
              <img width="100%" :src="dialogImageUrl" alt="">
            </el-dialog>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="cancelReply">取 消</el-button>
          <el-button type="primary" @click="submitReply" :loading="submitting">发 布</el-button>
        </span>
      </el-dialog>
    </div>
    <!-- 悬浮操作按钮 -->
    <div class="floating-actions">
      <button @click="refreshPage" class="action-btn refresh-btn" title="刷新页面">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
      <button @click="scrollToTop" class="action-btn top-btn" title="回到顶部">
        <i class="bi bi-chevron-up"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { getQuestionDetail, addReply, markAsSolved, deleteQuestion, deleteReply, markAsBestAnswer, likeQuestion, likeReply, getUserLikeStatus, unlikeQuestion, unlikeReply } from '@/api/helpforum';

export default {
  name: 'QuestionDetailView',
  data() {
    return {
      loading: true,
      question: null,
      replies: [],
      newReply: {
        content: '',
        images: []
      },
      submitting: false,
      dialogVisible: false,
      dialogImageUrl: '',
      rules: {
        content: [
          { required: true, message: '请输入回复内容', trigger: 'blur' },
          { min: 5, max: 1000, message: '回复内容长度在5到1000个字符之间', trigger: 'blur' }
        ]
      },
      windowWidth: window.innerWidth,
      showReplyDialog: false,
      questionLiked: false,
      replyLikedSet: new Set()
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.userInfo || {};
    },
    isAuthor() {
      return this.question && this.question.author_id === this.userInfo.email;
    },
    dialogWidth() {
      if (this.windowWidth <= 576) {
        return '95%';
      } else if (this.windowWidth <= 768) {
        return '85%';
      } else if (this.windowWidth <= 992) {
        return '70%';
      } else {
        return '50%';
      }
    },
    formLabelWidth() {
      return this.windowWidth <= 576 ? '60px' : '80px';
    }
  },
  created() {
    this.fetchQuestionDetail();
    window.addEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    question() {
      if (this.question) {
        this.checkUserLikes();
      }
    }
  },
  methods: {
    scrollToTop() {
      this.$refs.questionDetail.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    refreshPage() {
      this.fetchQuestionDetail()
    },
    // 获取问题详情
    fetchQuestionDetail() {
      this.loading = true;
      const id = this.$route.query.id;

      getQuestionDetail({ id }).then(res => {
        if (res.code === 200) {
          this.question = res.data;
          this.question.images = JSON.parse(this.question.images);
          // 确保每个回复都有 author 对象
          if (res.data.replies && Array.isArray(res.data.replies)) {
            this.replies = res.data.replies.map(reply => {
              return {
                ...reply,
                author: reply.author || { avatar: null, nickname: '匿名用户' }
              };
            });
          } else {
            this.replies = [];
          }
        } else {
          this.$message.error(res.message || '获取问题详情失败');
        }
        this.loading = false;
      }).catch(err => {
        console.error(err);
        this.$message.error('获取问题详情失败');
        this.loading = false;
      });
    },

    // 返回问题列表
    goBack() {
      this.$router.push('/helpforum');
    },

    // 编辑问题
    editQuestion() {
      this.$confirm('确定要编辑这个问题吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 实际项目中应该跳转到编辑页面或打开编辑对话框
        this.$message.info('编辑功能开发中...');
      }).catch(() => { });
    },

    // 删除问题
    deleteQuestion() {
      this.$confirm('确定要删除这个问题吗? 删除后将无法恢复!', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteQuestion({
          questionId: this.question.id,
          authorId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            this.$message.success('问题已删除');
            this.goBack();
          } else {
            this.$message.error(res.message || '删除失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('删除失败，请稍后重试');
        });
      }).catch(() => { });
    },

    /**
     * 编辑回复
     * @param {Object} reply - 要编辑的回复对象（目前未使用，保留以便将来实现）
     */
    // eslint-disable-next-line no-unused-vars
    editReply(reply) {
      // 注意：虽然reply参数目前未使用，但保留以便将来实现编辑功能时使用
      this.$confirm('确定要编辑这条回复吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 实际项目中应该打开编辑对话框
        this.$message.info('编辑功能开发中...');
      }).catch(() => { });
    },

    // 删除回复
    deleteReply(replyId) {
      this.$confirm('确定要删除这条回复吗? 删除后将无法恢复!', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteReply({
          replyId: replyId,
          authorId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            this.replies = this.replies.filter(r => r.id !== replyId);
            this.$message.success('回复已删除');
          } else {
            this.$message.error(res.message || '删除失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('删除失败，请稍后重试');
        });
      }).catch(() => { });
    },

    // 点赞/取消点赞回复
    toggleReplyLike(replyId) {
      if (!this.userInfo.email) {
        this.$message.warning('请先登录');
        return;
      }

      if (this.replyLikedSet.has(replyId)) {
        // 取消点赞
        unlikeReply({
          replyId: replyId,
          userId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            // 确保点赞数不小于0
            const replyIndex = this.replies.findIndex(r => r.id === replyId);
            if (replyIndex !== -1) {
              this.replies[replyIndex].likes = Math.max((this.replies[replyIndex].likes || 0) - 1, 0);
            }

            // 标记该回复已被取消点赞
            this.replyLikedSet.delete(replyId);

            this.$message.success('已取消点赞');
          } else {
            this.$message.error(res.message || '取消点赞失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('取消点赞失败，请稍后重试');
        });
      } else {
        // 点赞
        likeReply({
          replyId: replyId,
          userId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            // 更新回复的点赞数
            const replyIndex = this.replies.findIndex(r => r.id === replyId);
            if (replyIndex !== -1) {
              this.replies[replyIndex].likes = (this.replies[replyIndex].likes || 0) + 1;
            }

            // 标记该回复已被点赞
            this.replyLikedSet.add(replyId);

            this.$message.success('已点赞');
          } else {
            this.$message.error(res.message || '点赞失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('点赞失败，请稍后重试');
        });
      }
    },

    // 点赞/取消点赞问题
    toggleQuestionLike() {
      if (!this.userInfo.email) {
        this.$message.warning('请先登录');
        return;
      }

      if (this.questionLiked) {
        // 取消点赞
        unlikeQuestion({
          questionId: this.question.id,
          userId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            // 确保点赞数不小于0
            this.question.likes = Math.max((this.question.likes || 0) - 1, 0);
            this.questionLiked = false;
            this.$message.success('已取消点赞');
          } else {
            this.$message.error(res.message || '取消点赞失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('取消点赞失败，请稍后重试');
        });
      } else {
        // 点赞
        likeQuestion({
          questionId: this.question.id,
          userId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            this.question.likes = (this.question.likes || 0) + 1;
            this.questionLiked = true;
            this.$message.success('已点赞');
          } else {
            this.$message.error(res.message || '点赞失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('点赞失败，请稍后重试');
        });
      }
    },

    // 检查用户是否已点赞问题和回复
    checkUserLikes() {
      if (!this.userInfo.email || !this.question) {
        return;
      }

      getUserLikeStatus({
        userId: this.userInfo.email,
        questionId: this.question.id
      }).then(res => {
        if (res.code === 200) {
          // 设置问题点赞状态
          this.questionLiked = res.data.questionLiked;

          // 设置回复点赞状态
          this.replyLikedSet = new Set(res.data.replyLiked);
        } else {
          console.error('获取点赞状态失败:', res.message);
        }
      }).catch(err => {
        console.error('获取点赞状态失败:', err);
      });
    },

    // 采纳为最佳答案
    markAsSolution(replyId) {
      this.$confirm('确定采纳此回复为最佳答案吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        markAsBestAnswer({
          questionId: this.question.id,
          replyId: replyId,
          authorId: this.userInfo.email
        }).then(res => {
          if (res.code === 200) {
            this.question.solved = true;
            this.question.best_answer_id = replyId;
            this.$message.success('已采纳为最佳答案');
          } else {
            this.$message.error(res.message || '操作失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('操作失败，请稍后重试');
        });
      }).catch(() => { });
    },

    // 提交回复
    submitReply() {
      this.$refs.replyForm.validate((valid) => {
        if (valid) {
          this.submitting = true;

          const images = this.newReply.images.map(item => 'https://www.sweek.top/api/preview/' + item)
          const replyData = {
            questionId: this.question.id,
            content: this.newReply.content,
            images,
            authorId: this.userInfo.email
          };

          addReply(replyData).then(res => {
            this.submitting = false;
            if (res.code === 200) {
              this.showReplyDialog = false
              this.$message.success('回复成功');
              this.resetReplyForm();
              this.fetchQuestionDetail();

            } else {
              this.$message.error(res.message || '回复失败');
            }
          }).catch(err => {
            console.error(err);
            this.submitting = false;
            this.$message.error('回复失败，请稍后重试');
          });
        }
      });
    },

    // 取消回复
    cancelReply() {
      if (this.newReply.content || this.newReply.images.length > 0) {
        this.$confirm('确认取消？未保存的内容将会丢失', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.showReplyDialog = false;
          this.resetReplyForm();
        }).catch(() => { });
      } else {
        this.showReplyDialog = false;
      }
    },

    // 重置回复表单
    resetReplyForm() {
      this.newReply = {
        content: '',
        images: []
      };
      if (this.$refs.replyForm) {
        this.$refs.replyForm.resetFields();
      }
    },

    // 图片上传相关
    handleRemove(file) {
      console.log('file：：：+ ', file)
      const index = this.newReply.images.indexOf(file.response.filenames[0]);
      if (index !== -1) {
        this.newReply.images.splice(index, 1);
      }
    },
    handlePictureCardPreview(file) {
      console.log('file：：：+ ', file)
      this.dialogImageUrl = file.url || 'https://www.sweek.top/api/preview/' + file.response.filenames[0];
      this.dialogVisible = true;
    },
    handleUploadSuccess(res) {
      console.log('res：：：+ ', res)
      if (res.message == 'Files uploaded successfully') {
        this.newReply.images.push(res.filenames[0]);
      } else {
        this.$message.error('图片上传失败');
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.$message.error('只能上传图片文件!');
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!');
      }
      return isImage && isLt2M;
    },

    // 处理窗口大小变化
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 格式化时间
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      // 小于1分钟
      if (diff < 60 * 1000) {
        return '刚刚';
      }
      // 小于1小时
      if (diff < 60 * 60 * 1000) {
        return Math.floor(diff / (60 * 1000)) + '分钟前';
      }
      // 小于1天
      if (diff < 24 * 60 * 60 * 1000) {
        return Math.floor(diff / (60 * 60 * 1000)) + '小时前';
      }
      // 小于30天
      if (diff < 30 * 24 * 60 * 60 * 1000) {
        return Math.floor(diff / (24 * 60 * 60 * 1000)) + '天前';
      }

      // 大于30天显示具体日期
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 标记为已解决
    handleMarkAsSolved() {
      markAsSolved({
        questionId: this.question.id
      }).then(res => {
        if (res.code === 200) {
          this.$message.success('问题已标记为已解决');
          this.question.solved = true;
        } else {
          this.$message.error(res.message || '操作失败');
        }
      }).catch(err => {
        console.error(err);
        this.$message.error('操作失败，请稍后重试');
      });
    },

    handleDialogClose(done) {
      if (this.newReply.content || this.newReply.images.length > 0) {
        this.$confirm('确认关闭？未保存的内容将会丢失', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.resetReplyForm();
          done();
        }).catch(() => { });
      } else {
        done();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.question-detail {
  width: 100%;
  height: 100%;
  background-color: var(--bg-color);
  color: var(--text-color);
  border-radius: 12px;
  // box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: auto;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 10px;
  }

  .back-button {
    margin-bottom: 20px;
  }

  .loading-container {
    padding: 20px;
    background-color: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .not-found {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background-color: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    i {
      font-size: 4rem;
      color: #f56c6c;
      margin-bottom: 20px;
    }

    h2 {
      font-size: 1.5rem;
      color: var(--text-color);
      margin-bottom: 20px;
    }
  }

  .question-container {
    .question-header {
      margin-bottom: 20px;

      .question-title {
        font-size: 1.8rem;
        margin-bottom: 15px;
        color: var(--text-color);
        line-height: 1.3;
        word-break: break-word;

        @media (max-width: 768px) {
          font-size: 1.5rem;
        }
      }

      .question-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;

        .meta-item {
          display: flex;
          align-items: center;
          color: var(--secondary-text-color);

          i {
            margin-right: 5px;
          }
        }

        .status {
          padding: 4px 8px;
          border-radius: 4px;
          background-color: #f56c6c;
          color: white;

          &.solved {
            background-color: #67c23a;
          }
        }
      }
    }

    .question-content {
      background-color: var(--card-bg);
      border-radius: 12px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      padding: 20px;
      margin-bottom: 30px;
      border: 1px solid var(--border-color);

      @media (max-width: 768px) {
        padding: 15px;
      }

      .author-info {
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        .author-meta {
          margin-left: 15px;
          flex-grow: 1;

          .author-name {
            font-weight: 500;
            color: var(--text-color);
            font-size: 1.1rem;
          }

          .post-time {
            color: var(--secondary-text-color);
            font-size: 0.9rem;
          }
        }

        .question-actions {
          display: flex;
          gap: 10px;
        }
      }

      .content-body {
        p {
          line-height: 1.6;
          margin-bottom: 15px;
          white-space: pre-line;
          word-break: break-word;
        }

        .content-images {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;

          .el-image {
            width: 200px;
            max-width: 100%;
            border-radius: 4px;
            overflow: hidden;

            @media (max-width: 768px) {
              width: 150px;
            }
          }
        }

        .content-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .question-actions-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;

          .like-button {
            display: flex;
            align-items: center;
            padding: 10px;
            border: 1px solid #333;
            border-radius: 4px;

            i {
              margin-right: 5px;
            }

            &.liked {
              background-color: #e6f7ff;
              border-color: #1890ff;
              color: #1890ff;
            }
          }
        }
      }
    }

    .replies-section {
      margin-bottom: 30px;

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        h2 {
          font-size: 1.5rem;
          color: var(--text-color);
        }
      }

      .no-replies {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        background-color: var(--card-bg);
        border-radius: 12px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        border: 1px solid var(--border-color);

        i {
          font-size: 3rem;
          color: var(--secondary-text-color);
          margin-bottom: 15px;
        }

        p {
          font-size: 1.1rem;
          color: var(--text-color);
          margin-bottom: 15px;
        }
      }
    }

    .replies-list {
      .reply-item {
        display: flex;
        margin-bottom: 20px;
        background-color: var(--card-bg);
        border-radius: 12px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        border: 1px solid var(--border-color);
        padding: 20px;

        @media (max-width: 768px) {
          padding: 15px;
          flex-direction: column;
        }

        .reply-number {
          font-size: 1.2rem;
          font-weight: bold;
          color: var(--secondary-text-color);
          margin-right: 15px;
          min-width: 30px;

          @media (max-width: 768px) {
            margin-bottom: 10px;
          }
        }

        .reply-content {
          flex-grow: 1;

          .reply-author {
            display: flex;
            align-items: center;
            margin-bottom: 15px;

            @media (max-width: 768px) {
              // flex-wrap: wrap;
            }

            .author-meta {
              margin-left: 15px;
              flex-grow: 1;

              .author-name {
                font-weight: 500;
                color: var(--text-color);
                font-size: 1.1rem;
              }

              .post-time {
                color: var(--secondary-text-color);
                font-size: 0.9rem;
              }
            }

            .reply-actions {
              display: flex;
              gap: 10px;

              @media (max-width: 768px) {
                // width: 100%;
                margin-top: 10px;
                justify-content: flex-end;
              }
            }
          }

          .reply-body {
            p {
              line-height: 1.6;
              margin-bottom: 15px;
              white-space: pre-line;
              word-break: break-word;
            }

            .reply-images {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              margin-bottom: 15px;

              .el-image {
                width: 180px;
                max-width: 100%;
                border-radius: 4px;
                overflow: hidden;

                @media (max-width: 768px) {
                  width: 150px;
                }
              }
            }
          }

          .reply-footer {
            display: flex;
            align-items: center;
            margin-top: 15px;
            gap: 15px;
            flex-wrap: wrap;

            .like-button {
              display: flex;
              align-items: center;
              padding: 10px;
              border: 1px solid #333;
              border-radius: 4px;

              i {
                margin-right: 5px;
              }

              &.liked {
                background-color: #e6f7ff;
                border-color: #1890ff;
                color: #1890ff;
              }
            }

            .el-button {
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            }

            .solution-badge {
              display: flex;
              align-items: center;
              background-color: #67c23a;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 0.9rem;

              i {
                margin-right: 5px;
              }
            }
          }
        }
      }
    }
  }
}

// 回复对话框样式
.reply-dialog {
  .el-dialog__body {
    padding: 20px;

    @media (max-width: 768px) {
      padding: 15px;
    }
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .el-textarea {
    background-color: var(--input-bg);
    border-color: var(--border-color);
    color: var(--text-color);
  }

  .reply-form {
    padding: 20px;

    .el-form-item {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .reply-textarea {
      .el-textarea__inner {
        font-size: 14px;
        line-height: 1.6;
        padding: 12px 16px;
        border-radius: 8px;
      }
    }
  }

  .reply-image-upload {
    .el-upload--picture-card {
      width: 100px;
      height: 100px;
      line-height: 100px;
      margin-right: 12px;
      margin-bottom: 12px;
    }

    .el-upload-list--picture-card {
      .el-upload-list__item {
        width: 100px;
        height: 100px;
        margin-right: 12px;
        margin-bottom: 12px;
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #eee;

    .el-button {
      padding: 10px 24px;
      border-radius: 6px;
      font-weight: 500;
    }
  }

  @media (max-width: 768px) {
    .reply-form {
      padding: 16px;

      .reply-textarea .el-textarea__inner {
        font-size: 13px;
        padding: 10px 14px;
      }
    }

    .reply-image-upload {

      .el-upload--picture-card,
      .el-upload-list--picture-card .el-upload-list__item {
        width: 80px;
        height: 80px;
      }
    }
  }
}

// 悬浮操作按钮
.floating-actions {
  position: fixed;
  bottom: 4rem;
  right: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 100;
}

.action-btn {
  width: 3.25rem;
  height: 3.25rem;
  border: none;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s var(--transition-bounce);
  box-shadow: 0 10px 15px -3px var(--shadow-color),
    0 4px 6px -2px var(--shadow-color);
  background-color: var(--card-bg);
  color: var(--text-primary);
  font-size: 1.25rem;

  &:hover {
    transform: translateY(-5px);
  }

  &:active {
    transform: translateY(-2px);
  }
}

.pagination-container {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  width: 100%;

  :deep(.el-pagination) {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    padding: 10px 0;

    .el-pagination__total,
    .el-pagination__sizes,
    .el-pagination__jump {
      margin: 5px;
    }

    .el-pager li {
      min-width: 30px;
      height: 30px;
      line-height: 30px;
      margin: 0 2px;
    }

    .btn-prev,
    .btn-next {
      min-width: 30px;
      height: 30px;
      line-height: 30px;
      margin: 0 2px;
    }
  }
}

@media (max-width: 768px) {
  .pagination-container {
    :deep(.el-pagination) {

      .el-pagination__total,
      .el-pagination__sizes,
      .el-pagination__jump {
        width: 100%;
        text-align: center;
        margin: 5px 0;
      }

      .el-pager {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        width: 100%;
      }

      .el-pager li {
        min-width: 25px;
        height: 25px;
        line-height: 25px;
        margin: 2px;
      }

      .btn-prev,
      .btn-next {
        min-width: 25px;
        height: 25px;
        line-height: 25px;
        margin: 2px;
      }
    }
  }
}

@media (max-width: 576px) {
  .pagination-container {
    :deep(.el-pagination) {
      .el-pager li {
        min-width: 20px;
        height: 20px;
        line-height: 20px;
        margin: 1px;
      }

      .btn-prev,
      .btn-next {
        min-width: 20px;
        height: 20px;
        line-height: 20px;
        margin: 1px;
      }
    }
  }
}
</style>
